import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const ChartCurrencyBreakdown: React.FC = () => {
  const { orders, transactions, baseCurrency, isCustomRangePending } =
    useAnalyticsContext();

  const breakdown =
    orders.data?.currency?.breakdown ??
    transactions.data?.currency?.breakdown ??
    [];

  const data = useMemo(
    () =>
      breakdown
        .map(item => ({
          currency: item.currency,
          original: Number(item.totalAmount || 0),
          converted: Number(item.totalAmountConverted || 0),
        }))
        .filter(item => item.original > 0 || item.converted > 0),
    [breakdown],
  );

  const totalConverted = data.reduce(
    (sum, item) => sum + item.converted,
    0,
  );

  return (
    <Card className="border-none h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Revenue by Currency
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Converted to {baseCurrency.code} ({baseCurrency.symbol}) using recorded
          exchange rates.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {orders.isLoading ? (
          <div className="flex h-[260px] items-center justify-center">
            <div className="h-48 w-full animate-pulse rounded-xl bg-muted/50" />
          </div>
        ) : isCustomRangePending ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Select a start and end date to view custom range insights.
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            No currency variance detected for the selected period.
          </div>
        ) : (
          <>
            <div className="h-[220px]">
              <ResponsiveContainer>
                <BarChart
                  data={data}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <defs>
                    <linearGradient
                      id="currencyOriginalGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.4} />
                    </linearGradient>
                    <linearGradient
                      id="currencyConvertedGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="var(--primary)"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="100%"
                        stopColor="var(--primary)"
                        stopOpacity={0.4}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="4 4"
                    className="stroke-muted/50"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="currency"
                    tickLine={false}
                    axisLine={true}
                    tickMargin={8}
                    className="text-xs text-muted-foreground"
                  />
                  <YAxis
                    tickMargin={8}
                    axisLine={true}
                    tickLine={false}
                    className="text-xs text-muted-foreground"
                    tickFormatter={value =>
                      value.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })
                    }
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }),
                      name === 'original' ? 'Local currency' : 'Converted',
                    ]}
                  />
                  <Bar
                    dataKey="original"
                    name="Local currency"
                    fill="url(#currencyOriginalGradient)"
                    radius={[6, 6, 0, 0]}
                    barSize={22}
                  />
                  <Bar
                    dataKey="converted"
                    name={`Converted (${baseCurrency.code})`}
                    fill="url(#currencyConvertedGradient)"
                    radius={[6, 6, 0, 0]}
                    barSize={22}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-lg border bg-card/50 p-3">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Total converted revenue</span>
                <span className="font-semibold text-foreground">
                  {baseCurrency.symbol}
                  {totalConverted.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ChartCurrencyBreakdown;

